import React, { useEffect, useRef, useState } from 'react';

interface ProgressRingProps {
	radiusEm: number;
	strokeEm: number;
	progress: number;
	className?: string;
}

const ProgressRing: React.FC<ProgressRingProps> = ({ className, radiusEm, strokeEm, progress }) => {
	const normalizedRadius = useRef<number>(0);
	const [circumference, setCircumference] = useState<number>(0);
	const [strokeDashoffset, setStrokeDashoffset] = useState<number>(0);

	const [fontSizeInPixels, setFontSizeInPixels] = useState<number>(0);

	const [radius, setRadius] = useState<number>(0);
	const [stroke, setStroke] = useState<number>(0);

	const elementRef = useRef(null);

	useEffect(() => {
		const getFontSizeInPixels = () => {
			if (elementRef.current) {
				// Get the computed style of the element
				const fontSize = window.getComputedStyle(elementRef.current).fontSize;
				return parseFloat(fontSize);
			}

			return 0;
		}

		const compute = () => {
			if (elementRef.current && fontSizeInPixels > 0) {
				const radius = radiusEm * fontSizeInPixels;
				const stroke = strokeEm * fontSizeInPixels;

				normalizedRadius.current = radius - stroke * 2;
				const circumference = normalizedRadius.current * 2 * Math.PI;

				const strokeDashoffset = circumference - (progress / 100) * circumference;

				setRadius(radius);
				setStroke(stroke);
				setCircumference(circumference);
				setStrokeDashoffset(strokeDashoffset);
			}
		}

		compute();

		if (fontSizeInPixels === 0)
			setFontSizeInPixels(getFontSizeInPixels());

		const handleResize = () => {
			if (elementRef.current) {
				const newFontSizeInPixels = getFontSizeInPixels();

				if (newFontSizeInPixels !== fontSizeInPixels) {
					setFontSizeInPixels(newFontSizeInPixels);
					compute();
				}
			}
		}

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [fontSizeInPixels, progress, radiusEm, stroke, strokeDashoffset, strokeEm]);

	return (
		<svg
			className={`${className}`}
			style={{ display: radius > 0 ? "block" : "none" }}
			ref={elementRef}
			height={radius * 2}
			width={radius * 2}>
			<circle
				stroke="white"
				fill="transparent"
				strokeWidth={stroke}
				strokeDasharray={`${circumference} ${circumference}`}
				style={{ strokeDashoffset: strokeDashoffset }}
				r={normalizedRadius.current}
				cx={radius}
				cy={radius} />
		</svg>
	);
};

export default ProgressRing;